import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBrandManager } from "@/hooks/useBrandManager";
import { toast } from "@/hooks/use-toast";
import { usePageVisibility } from "@/hooks/usePageVisibility";

export interface InAppNotification {
  id: string;
  user_id: string;
  brand_id: string;
  task_id: string;
  task_table: string;
  trigger_type: string;
  title: string;
  message: string;
  task_title: string;
  previous_status?: string;
  new_status?: string;
  changed_by_user_id?: string;
  read_at?: string;
  dismissed_at?: string;
  created_at: string;
  metadata?: any;
}

export const useInAppNotifications = () => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    if (!user || !currentBrandId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('tasks_notifications_in_app')
        .select('*')
        .eq('user_id', user.id)
        .eq('brand_id', currentBrandId)
        .is('dismissed_at', null)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching notifications:', error);
        toast({
          title: "Error",
          description: "Failed to fetch notifications",
          variant: "destructive",
        });
        return;
      }

      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.read_at).length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('tasks_notifications_in_app')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user?.id);

      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }

      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, read_at: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user || !currentBrandId) return;

    try {
      const { error } = await supabase
        .from('tasks_notifications_in_app')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('brand_id', currentBrandId)
        .is('read_at', null);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        return;
      }

      setNotifications(prev => 
        prev.map(n => ({ ...n, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const dismissNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('tasks_notifications_in_app')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user?.id);

      if (error) {
        console.error('Error dismissing notification:', error);
        return;
      }

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Update unread count if the dismissed notification was unread
      const dismissedNotification = notifications.find(n => n.id === notificationId);
      if (dismissedNotification && !dismissedNotification.read_at) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user, currentBrandId]);

  // Use page visibility to pause subscription when tab is hidden
  const { isVisible } = usePageVisibility();

  // Set up realtime subscription for new notifications (visibility-aware)
  useEffect(() => {
    if (!user || !currentBrandId || !isVisible) return;

    const channel = supabase
      .channel(`notifications-updates-${currentBrandId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tasks_notifications_in_app',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as InAppNotification;
          if (newNotification.brand_id === currentBrandId) {
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast for new notification
            toast({
              title: newNotification.title,
              description: newNotification.message,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, currentBrandId, isVisible]);
  
  // Refetch when tab becomes visible after being hidden
  useEffect(() => {
    if (isVisible && user && currentBrandId) {
      fetchNotifications();
    }
  }, [isVisible]);

  return {
    notifications,
    isLoading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    refetch: fetchNotifications,
  };
};